import { useTranslation } from 'react-i18next'
import { isNil } from 'ramda'
import { ddMMyyyy, formatMoney } from '@/utils'
import { formatRiskAddress } from '@/utils/address'
import { SearchCriteria } from '@/api/recommendations'

type Employees = {
  ownersCount: number | null
  fullTimeEmployeesCount: number | null
  partTimeEmployeesCount: number | null
  miniJobbersCount: number | null
}

const isAssessmentValid = (assessmentData: Record<string, unknown>) => {
  return Object.values(assessmentData).every(Boolean)
}

export const useRiskAdditionalInfo = (
  data:
    | {
        companyFoundedDate: { foundedDate: string } | null
        lastYearFinances: {
          approxTurnover: number | null
          approxPersonelExpenses: number | null
        } | null
        employeesCount: Employees | null
        inventorySum: number | null
        riskAddress: {
          street: string
          streetNumber: string
          postalCode: string
          city: string
        } | null
        startDate: {
          startDate: string
        }
      }
    | undefined,
  categoryKey: string,
  searchCriteria?: SearchCriteria,
  companyFoundedDate?: string | null,
): { additionalInfo: string | null } => {
  const { t } = useTranslation()

  const formatEmployees = (employees: Employees) => {
    const employeeTypes: { key: keyof Employees; label: string }[] = [
      { key: 'ownersCount', label: t('companyPage.usersRoles.owner') },
      { key: 'fullTimeEmployeesCount', label: t('full_time_employees') },
      { key: 'partTimeEmployeesCount', label: t('part_time_employees') },
      { key: 'miniJobbersCount', label: t('mini_jobbers') },
    ]

    return employeeTypes
      .map((type) => {
        if (!isNil(employees[type.key])) {
          return `${employees[type.key]}x ${t(type.label)}`
        }
        return null
      })
      .filter(Boolean)
      .join(', ')
  }

  // why this is nested fn? change to inline
  const parseRiskData = (): string | null => {
    if (
      !['liability', 'financialLoss', 'content', 'legal'].includes(categoryKey)
    )
      return null

    const foundedDateSource =
      companyFoundedDate ?? data?.companyFoundedDate?.foundedDate
    const foundedDate = foundedDateSource && ddMMyyyy(foundedDateSource)
    const turnoverSource =
      searchCriteria?.approxTurnover ?? data?.lastYearFinances?.approxTurnover
    const turnover = turnoverSource != null ? formatMoney(turnoverSource) : null
    const salariesSource =
      searchCriteria?.salary ?? data?.lastYearFinances?.approxPersonelExpenses
    const salaries = salariesSource != null ? formatMoney(salariesSource) : null
    const employeesSource = searchCriteria
      ? {
          ownersCount:
            searchCriteria.ownersCount ??
            data?.employeesCount?.ownersCount ??
            null,
          fullTimeEmployeesCount:
            searchCriteria.fullTimeEmployeesCount ??
            data?.employeesCount?.fullTimeEmployeesCount ??
            null,
          partTimeEmployeesCount:
            searchCriteria.partTimeEmployeesCount ??
            data?.employeesCount?.partTimeEmployeesCount ??
            null,
          miniJobbersCount:
            searchCriteria.miniJobbersCount ??
            data?.employeesCount?.miniJobbersCount ??
            null,
        }
      : data?.employeesCount
    const employees = employeesSource && formatEmployees(employeesSource)
    const inventorySum = data?.inventorySum && formatMoney(data.inventorySum)
    const riskAddress = data?.riskAddress && formatRiskAddress(data.riskAddress)
    const startDateSource =
      searchCriteria?.startDate ?? data?.startDate?.startDate
    const startDate = startDateSource && ddMMyyyy(startDateSource)

    if (categoryKey === 'liability' || categoryKey === 'financialLoss') {
      const assessmentData = {
        turnover,
        foundedDate,
        employees,
        salaries,
      }

      return isAssessmentValid(assessmentData)
        ? t('recommendations.additionalAssessmentInfo', assessmentData)
        : null
    }

    if (categoryKey === 'content') {
      const assessmentData = {
        turnover,
        foundedDate,
        employees,
        inventorySum,
        riskAddress,
      }
      return isAssessmentValid(assessmentData)
        ? t('recommendations.additionalAssessmentInfoContent', assessmentData)
        : null
    }

    if (categoryKey === 'legal') {
      const assessmentData = {
        turnover,
        foundedDate,
        employees,
        ...(startDate && { startDate }),
      }

      if (startDate) {
        return isAssessmentValid(assessmentData)
          ? t('recommendations.additionalAssessmentInfoLegal', assessmentData)
          : null
      } else {
        return isAssessmentValid(assessmentData)
          ? t(
              'recommendations.additionalAssessmentInfoLegalNoStartDate',
              assessmentData,
            )
          : null
      }
    }

    return null
  }

  return {
    additionalInfo: parseRiskData(),
  }
}
